"use client";

import { motion } from "motion/react";
import { DateTimeSelector } from "./DateTimeSelector";
import Icon from "@hudson/ui/components/Icon";
import useContent from "@lib/use_content";
import useWindowSize from "@utils/use_window_size";
import { isMobile } from "@hudson/ui/lib/utils";
import useConfig from "@lib/use_config";

function PickupOrFlightDateTimeSelector({
  className,
  disabled,
  fieldName,
  flightType,
  hideIcon,
  hideTime,
  isFlight,
  openToDate,
  timeDefault,
}: {
  className?: string;
  disabled?: boolean;
  fieldName: string;
  flightType?: "arrival" | "departure";
  hideIcon?: boolean;
  hideTime?: boolean;
  isFlight: boolean;
  openToDate?: Date;
  timeDefault?: string;
}): JSX.Element | null {
  useWindowSize(); // Rerenders the component on window size change.
  const { loading: cfgLoading, data: cfgData } = useConfig(["dateFormat", "timeFormat"]);
  const { data } = useContent([
    "bePickupDateLabel",
    "bePickupTimeLabel",
    "beFlightDateLabel",
    "beFlightDateArrLabel",
    "beFlightDateDepLabel",
    "beFlightTimeLabel",
    "beFlightTimeArrLabel",
    "beFlightTimeDepLabel",
  ]);

  const iconVariants = {
    flight: {
      opacity: 1,
      display: "block",
    },
    pickup: {
      opacity: 0,
      display: "none",
    },
  };

  // Determine labels.
  let timeLabel = data?.content.bePickupTimeLabel,
    dateLabel = data?.content.bePickupDateLabel;
  if (isFlight && data?.content) {
    if (flightType === "arrival") {
      dateLabel = data.content.beFlightDateArrLabel || data.content.beFlightDateLabel;
      timeLabel = data.content.beFlightTimeArrLabel || data.content.beFlightTimeLabel;
    } else if (flightType === "departure") {
      dateLabel = data.content.beFlightDateDepLabel || data.content.beFlightDateLabel;
      timeLabel = data.content.beFlightTimeDepLabel || data.content.beFlightTimeLabel;
    }
  }

  return (
    <div className={`inline-flex relative z-10 ${className}`}>
      {!hideIcon && !isMobile(600) && (
        <motion.div
          variants={iconVariants}
          initial="pickup"
          animate={isFlight ? "flight" : "pickup"}
        >
          <Icon type="plane" muted className="h-7 mr-4 ml-2 mt-2" />
        </motion.div>
      )}

      <DateTimeSelector
        fieldName={fieldName}
        dateFormat={cfgData?.config.dateFormat}
        dateLabel={dateLabel}
        disabled={disabled}
        hideTime={hideTime}
        loading={cfgLoading}
        openToDate={openToDate}
        timeDefault={timeDefault}
        timeFormat={cfgData?.config.timeFormat}
        timeLabel={timeLabel}
      />
    </div>
  );
}

export default PickupOrFlightDateTimeSelector;
